import styled, { css, ThemedStyledProps } from 'styled-components';

import { DefaultTheme } from 'styled-components';
import {
  CapsuleStyleProps,
  InnerStyleProps,
  LabelStyleProps,
  Space,
  VerticalSpaceProps,
} from './types';

const toSpace = (v?: Space) =>
  v == null ? undefined : typeof v === 'number' ? `${v}px` : v;

type TP<P> = ThemedStyledProps<P, DefaultTheme>;

type RootStyleProps = VerticalSpaceProps;

const filterProps = (prop: string) =>
  ![
    'lineStyle',
    'lineColor',
    'thickness',
    'lineWidth',
    'align',
    'position',
    'raw',
    'bg',
    'color',
    'fontSize',
    'fontWeight',
    'paddingX',
    'radius',
    'padding',
    'hasBorder',
    'borderStyle',
    'borderColor',
  ].includes(prop);

export const Root = styled.div.withConfig({
  // evita enviar props customizadas ao DOM
  shouldForwardProp: (prop, defaultValidator) =>
    !['margin', 'marginTop', 'marginBottom'].includes(prop) &&
    defaultValidator(prop),
})<RootStyleProps>`
  position: relative;
  width: 100%;
  display: block;

  /* margin-top: priority => marginTop ?? margin ?? 0 */
  margin-top: ${({ margin, marginTop }: TP<RootStyleProps>) =>
    toSpace(marginTop ?? margin) ?? '0'};

  /* margin-bottom: priority => marginBottom ?? margin ?? 0 */
  margin-bottom: ${({ margin, marginBottom }: TP<RootStyleProps>) =>
    toSpace(marginBottom ?? margin) ?? '0'};
`;

export const Inner = styled.div.withConfig({
  shouldForwardProp: (prop, defaultValidator) =>
    filterProps(prop) && defaultValidator(prop),
})<InnerStyleProps>`
  position: relative;
  width: ${({ lineWidth }) => lineWidth};
  ${({ align }) =>
    align === 'center'
      ? css`
          margin-left: auto;
          margin-right: auto;
        `
      : align === 'right'
      ? css`
          margin-left: auto;
          margin-right: 0;
        `
      : css`
          margin-left: 0;
          margin-right: auto;
        `}

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    border-top-style: ${({ lineStyle }) => lineStyle};
    border-top-color: ${({ lineColor }) => lineColor};
    border-top-width: ${({ thickness }) => `${thickness}px`};
    transform: translateY(-50%);
    z-index: 0;
  }

  padding: 0.5rem 0;
`;

export const Label = styled.span.withConfig({
  shouldForwardProp: (prop, defaultValidator) =>
    !['position', 'bg', 'color', 'fontSize', 'fontWeight', 'paddingX'].includes(
      prop,
    ) && defaultValidator(prop),
})<LabelStyleProps>`
  position: absolute;
  top: 50%;

  ${({ position }: TP<LabelStyleProps>) =>
    position === 'left'
      ? css`
          left: 0;
          transform: translateY(-50%);
          text-align: left;
        `
      : position === 'right'
      ? css`
          right: 0;
          transform: translateY(-50%);
          text-align: right;
        `
      : css`
          left: 50%;
          transform: translate(-50%, -50%);
          text-align: center;
        `}

  padding: 0 ${({ paddingX }: TP<LabelStyleProps>) => paddingX};
  background-color: ${({ bg }: TP<LabelStyleProps>) => bg};
  color: ${({ color }: TP<LabelStyleProps>) => color};
  font-size: ${({ fontSize }: TP<LabelStyleProps>) => fontSize};
  font-weight: ${({ fontWeight }: TP<LabelStyleProps>) => fontWeight};
  line-height: 1.25;
  z-index: 1;
  white-space: nowrap;
`;

export const ChildrenCapsule = styled.div.withConfig({
  shouldForwardProp: (prop, defaultValidator) =>
    ![
      'position',
      'raw',
      'bg',
      'radius',
      'padding',
      'hasBorder',
      'borderStyle',
      'borderColor',
    ].includes(prop) && defaultValidator(prop),
})<CapsuleStyleProps>`
  position: absolute;
  top: 50%;

  ${({ position }: TP<CapsuleStyleProps>) =>
    position === 'left'
      ? css`
          left: 0;
          transform: translateY(-50%);
          text-align: left;
        `
      : position === 'right'
      ? css`
          right: 0;
          transform: translateY(-50%);
          text-align: right;
        `
      : css`
          left: 50%;
          transform: translate(-50%, -50%);
          text-align: center;
        `}

  z-index: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  /* Modo 'children puro' (sem cápsula) */
  ${({ raw }: TP<CapsuleStyleProps>) =>
    raw
      ? css`
          background: transparent;
          border: none;
          border-radius: 0;
          padding: 0;
        `
      : css`
          background-color: ${({ bg }: TP<CapsuleStyleProps>) => bg};
          border-radius: ${({ radius }: TP<CapsuleStyleProps>) => radius};
          padding: ${({ padding }: TP<CapsuleStyleProps>) => padding};
          ${({ hasBorder, borderStyle, borderColor }: TP<CapsuleStyleProps>) =>
            hasBorder
              ? css`
                  border: 1px ${borderStyle} ${borderColor};
                `
              : css`
                  border: none;
                `}
        `}
`;
